<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useProjectStore } from '@/stores/project'
import { useUserStore } from '@/stores/user'
import { useAccessStore } from '@/stores/access'
import { ChevronDown, Link, UserRoundPlus } from 'lucide-vue-next'

import { Invite } from '@/types'
import type Project from '@/types/Project'
import { generateDisplayName } from '@/utils/user'
import { flattenProjectsWithLevel } from '@/utils/projectTree'
import { joinsSubProjects } from '@/utils/accessDomains'
import {
  Dialog,
  DialogContent,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { useQrCode } from '@/composables/useQrCode'
import MemberSearchInput from '@/components/member/MemberSearchInput.vue'
import MemberInviteList, { missingName } from '@/components/member/MemberInviteList.vue'
import MemberManualEntryForm from '@/components/member/MemberManualEntryForm.vue'
import InviteProjectAccess, { accessSummary } from '@/pages/Project/Members/InviteProjectAccess.vue'
import type { SearchMember } from '@/components/member/MemberSearchInput.vue'
import type { InviteRowItem, RoleOption } from '@/components/member/MemberInviteList.vue'
import type { ManualEntryValues } from '@/components/member/MemberManualEntryForm.vue'

const projectStore = useProjectStore()

const userStore = useUserStore()

const accessStore = useAccessStore()

const PROJECT_ROLES = computed<RoleOption[]>(() => accessStore.roleChoices('project'))
const defaultRoleId = computed<number | null>(() => accessStore.defaultJoinRoleId)
const roleLabel = (role: string | number) => accessStore.role(role)?.name ?? ''
const route = useRoute()

const currentMember = computed(() => userStore.member)
const projectId = route.params.project_id as string
const project = computed(() => projectStore.current)
const orgId = computed(() => project.value?.organization_id)
const flatedProjects = computed<Project[]>(() => projectStore.flatedProjects || [])

const subProjects = computed(() => {
  const byParent = new Map<number, Project[]>()
  for (const p of flatedProjects.value) {
    if (p.parent_id == null) continue
    if (!byParent.has(p.parent_id)) byParent.set(p.parent_id, [])
    byParent.get(p.parent_id)!.push(p)
  }
  const descendants: Project[] = []
  const stack = [Number(projectId)]
  while (stack.length) {
    const id = stack.pop()!
    for (const child of byParent.get(id) || []) {
      descendants.push(child)
      stack.push(child.project_id)
    }
  }
  return flattenProjectsWithLevel(descendants)
})

const newRowDefaults = () => ({
  join_project_ids: [] as number[],
  settings_open: false,
})

const isOpen = ref(false)
const isInviteInProgress = ref(false)
const isLinkCopied = ref(false)
const invite = ref<Invite | null>(null)
const showManualEntry = ref(false)
const inviteRows = ref<InviteRowItem[]>([])
const manualRole = ref<string | number>('')
const manualInitialContact = ref('')


const inviteLink = computed(() => (invite.value ? urlFromUuid(invite.value.uuid) : null))
const { svg: qrSvg } = useQrCode(() => inviteLink.value)

const close = () => {
  isOpen.value = false
  invite.value = null
  inviteRows.value = []
  showManualEntry.value = false
  isInviteInProgress.value = false
  isLinkCopied.value = false
  manualInitialContact.value = ''
}

watch(isOpen, (newValue) => {
  if (!newValue) {
    invite.value = null
    inviteRows.value = []
    showManualEntry.value = false
    isInviteInProgress.value = false
    isLinkCopied.value = false
    manualInitialContact.value = ''
  }
})

const copyLink = () => {
  if (!inviteLink.value) return
  navigator.clipboard.writeText(inviteLink.value)
  isLinkCopied.value = true
  setTimeout(() => {
    isLinkCopied.value = false
  }, 1000)
}

const urlFromUuid = (uuid: string) => {
  return `${location.protocol}//${window.location.host}/invite/${uuid}`
}

const onSelectMember = (member: SearchMember) => {
  if (inviteRows.value.some(r => r.member_id === member.member_id)) return

  inviteRows.value.push({
    member_id: member.member_id,
    first_name: member.first_name || '',
    last_name: member.last_name || '',
    email: member.email || null,
    phone: member.phone || null,
    avatar_url: member.avatar_url || null,
    display_name: member.display_name || null,
    verified: member.verified ?? false,
    role: defaultRoleId.value ?? '',
    ...newRowDefaults(),
  })
}

const onAddNew = (contact = '') => {
  manualInitialContact.value = contact
  showManualEntry.value = true
}

const onManualSubmit = (values: ManualEntryValues, role: string | number) => {
  inviteRows.value.push({
    member_id: null,
    first_name: values.first_name,
    last_name: values.last_name,
    email: values.email,
    phone: values.phone,
    avatar_url: null,
    display_name: generateDisplayName(values.first_name, values.last_name),
    role,
    ...newRowDefaults(),
  })

  showManualEntry.value = false
  manualRole.value = defaultRoleId.value ?? ''
  manualInitialContact.value = ''
}

const onManualCancel = () => {
  showManualEntry.value = false
  manualInitialContact.value = ''
}

const removeRow = (index: number) => {
  inviteRows.value.splice(index, 1)
}

const updateRole = (index: number, role: string | number) => {
  inviteRows.value[index].role = role
}

const updateRowAccess = (index: number, patch: Partial<InviteRowItem>) => {
  Object.assign(inviteRows.value[index], patch)
}

const onSubmit = async () => {
  if (inviteRows.value.length === 0) return

  isInviteInProgress.value = true
  try {
    let lastInvite: Invite | null = null
    const failedRows: InviteRowItem[] = []
    for (const row of inviteRows.value) {
      const payload: any = {
        organization_id: orgId.value,
        inviter_id: currentMember.value.member_id,
        project_id: projectId,
        role_id: row.role,
        join_project_ids: joinsSubProjects(accessStore.role(row.role)) ? [] : row.join_project_ids,
        email: row.email,
        phone: row.phone,
        first_name: row.first_name,
        last_name: row.last_name,
        fullname: row.display_name || generateDisplayName(row.first_name || '', row.last_name || ''),
      }
      const result = await projectStore.addInvite(payload)
      if (result) {
        lastInvite = result
      } else {
        failedRows.push(row)
      }
    }
    if (failedRows.length > 0) {
      inviteRows.value = failedRows
    } else if (inviteRows.value.length === 1 && lastInvite) {
      invite.value = lastInvite
    } else {
      close()
    }
  } finally {
    isInviteInProgress.value = false
  }
}
</script>

<template>
  <Dialog :open="isOpen" @update:open="isOpen = $event">
    <DialogTrigger as-child>
      <slot>
        <Button variant="ghost" class="h-8 px-2 flex justify-start" data-testid="add-member-button">
          <UserRoundPlus class="w-5 h-5 text-muted-foreground" />
        </Button>
      </slot>
    </DialogTrigger>

    <DialogContent class="max-w-[520px]">
      <div data-testid="invite-member-dialog">
        <DialogHeader v-if="!invite">
          <DialogTitle class="mb-0.5">Invite a New Member</DialogTitle>
          <DialogDescription v-if="project?.project_name">
            to {{ project.project_name }}
          </DialogDescription>
        </DialogHeader>

      <div v-if="invite" class="text-center pt-3" data-testid="invite-success-section">
        <div class="mb-5">
          <h3 class="font-bold text-lg">Invite Created</h3>
          <p class="text-sm">Copy and share the invite with the person.</p>

          <div v-if="invite.email && invite.email_status === 'sent'" class="mt-3">
            <p class="text-sm">
              The invite will also be sent to
              <span class="font-semibold">{{ invite.email }}</span>
            </p>
          </div>
        </div>
        <div v-if="inviteLink">
          <div
            class="mx-auto mb-4 w-full max-w-[260px] rounded-2xl bg-white p-3 ring-1 ring-black/10 [&>svg]:h-auto [&>svg]:w-full"
            data-testid="invite-qr-code"
            v-html="qrSvg"
          ></div>
          <Input :model-value="inviteLink" readonly @click="copyLink" class="cursor-pointer" data-testid="invite-link-input" />
          <div class="mt-2 flex justify-center space-x-2">
            <Button variant="secondary" class="flex items-center" @click="copyLink" data-testid="copy-link-button">
              <Link class="w-3 h-3 mr-1" />
              <span v-if="!isLinkCopied">Copy invite</span>
              <span v-else>Invite copied!</span>
            </Button>
            <Button variant="ghost" @click="close" data-testid="invite-close-button">
              Close
            </Button>
          </div>
        </div>
      </div>

      <div v-else class="space-y-4 mt-2">
        <div v-if="orgId" class="space-y-1.5">
          <MemberSearchInput
            :org-id="orgId"
            :project-id="projectId"
            :placeholder="inviteRows.length > 0 ? 'Add another person…' : undefined"
            @select="onSelectMember"
            @add-new="onAddNew"
          />
          <p class="text-xs text-muted-foreground">
            {{ inviteRows.length > 0 ? 'You can invite several people at once' : 'Search by name, email or phone' }}
          </p>
        </div>

        <MemberManualEntryForm
          v-if="showManualEntry"
          v-model:role="manualRole"
          :roles="PROJECT_ROLES"
          :default-role="defaultRoleId ?? ''"
          mode="emailOrPhone"
          :initial-contact="manualInitialContact"
          @submit="onManualSubmit"
          @cancel="onManualCancel"
        />

        <div class="max-h-[380px] overflow-y-auto">
          <MemberInviteList
            require-names
            :rows="inviteRows"
            :roles="PROJECT_ROLES"
            @remove="removeRow"
            @update:role="updateRole"
          >
            <template #row-trailing="{ row, index }">
              <Button
                variant="ghost"
                size="sm"
                class="h-7 gap-1 px-2 text-xs text-muted-foreground shrink-0"
                @click="updateRowAccess(index, { settings_open: !row.settings_open })"
              >
                {{ roleLabel(row.role) }} · {{ accessSummary(row, joinsSubProjects(accessStore.role(row.role))) }}
                <ChevronDown class="w-3.5 h-3.5 transition-transform" :class="{ 'rotate-180': row.settings_open }" />
              </Button>
            </template>
            <template #row-extra="{ row, index }">
              <InviteProjectAccess
                v-if="row.settings_open"
                :row="row"
                :current-project-name="project?.project_name || ''"
                :sub-projects="subProjects"
                @update:row="patch => updateRowAccess(index, patch)"
              />
            </template>
          </MemberInviteList>
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" @click="close" data-testid="invite-close-button">Close</Button>
          <Button @click="onSubmit" :disabled="inviteRows.length === 0 || inviteRows.some(r => !r.role || missingName(r)) || isInviteInProgress" data-testid="invite-create-button">
            <Spinner v-if="isInviteInProgress" class="w-4 h-4" />
            <span v-else>{{ inviteRows.length > 1 ? `Invite (${inviteRows.length})` : 'Invite' }}</span>
          </Button>
        </DialogFooter>
      </div>
      </div>
    </DialogContent>
  </Dialog>
</template>
